import React, { useCallback, useMemo, useRef, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Box, Slide, Stack, Typography } from "@mui/material";
import { AquariusButton } from "@smartrr/shared/customerPortal/components/AquariusButton";
import { ConfirmCancelButtons } from "@smartrr/shared/customerPortal/components/ConfirmCancelButtons";
import { DotSpinner } from "@smartrr/shared/customerPortal/components/DotSpinner";
import { ICancellationReason, IPauseReason, IUserPause, PauseActionType } from "@smartrr/shared/entities/CancellationRelationship";
import { IPurchaseState } from "@smartrr/shared/entities/PurchaseState";
import { ScheduledDeliveryGroupWithPurchSt } from "@smartrr/shared/entities/PurchaseState/scheduleUtils";
import { adjustToStoreBillingTime, resetTimeOnDate, updateDateRecur } from "@smartrr/shared/utils/dateUtils";
import { localize, typedLocalize } from "@smartrr/shared/utils/sharedTranslations/localize";
import { useDisabledDatesByBillingDays } from "@customer-app/account/views/OperationAquarius/hooks/useDisabledDatesByBillingDays";
import { useCloseModal } from "@customer-app/state/actionCreators/modal";
import { updateNextShipmentDate } from "@smartrr/customer-portal/src/_state/actionCreators/customerPurchaseStates";
import {
  useSmartrrCustomerDispatch,
  useSmartrrCustomerSelector,
} from "@smartrr/customer-portal/src/_state/typedCustomerReduxHooks";
import { AquariusDatePicker } from "./AquariusDatePicker";
import { saveUserPauseReasonAPI } from "@customer-app/state/actionCreators/retention";
import { captureException } from "@smartrr/shared/utils/captureException";
import { CenterText } from "./CenterText";
import PauseReasonPicker from "./PauseReasonPicker";

export interface IDelayDateViewProps {
  setIconButtonInactive: () => void;
  delivery: ScheduledDeliveryGroupWithPurchSt<IPurchaseState>;
  saveUserCancellationData?: (cancel?: boolean) => void;
  cancellationReason?: ICancellationReason | undefined;
}
type DelayDateViewPhase = "reason-picker" | "delay-picker";

export const DelayDateView = ({
  delivery,
  setIconButtonInactive,
  saveUserCancellationData,
  cancellationReason,
}: IDelayDateViewProps): JSX.Element => {
  const dispatch = useSmartrrCustomerDispatch();
  const closeModal = useCloseModal();
  const localizeShorthand = "subscriptions.yourSubscriptions.orderOptionsModal";
  const organization = useSmartrrCustomerSelector(state => state.auth.organization);
  const [showDatePicker, setShowDatePicker] = useState(false);
  const [isUpdatingNextDeliveryDate, setIsUpdatingNextDeliveryDate] = useState(false);
  const [active, setActive] = useState<number>(-1);
  const [selectedTime, setSelectedTime] = useState<Date | null>(null);
  const dateBoxRef = useRef(null);

  const [phase, setPhase] = useState<DelayDateViewPhase>("delay-picker");
  const [selectedPauseReason, setSelectedPauseReason] = useState<IPauseReason>();
  const [userPauseReasonText, setUserPauseReasonText] = useState<string>();
  const { isEnabled: hasRetention, pauseReasons } = useSmartrrCustomerSelector(state => state.retention);

  const isRetentionAvailable =
    hasRetention &&
    pauseReasons?.length &&
    !!organization?.hasRetentionPauseReasonEnabled &&
    !!organization?.hasRetentionSetNextOrderReasonEnabled;

  useEffect(() => {
    setPhase(isRetentionAvailable ? "reason-picker" : "delay-picker");
  }, [isRetentionAvailable]);

  const nextDeliveryDate = delivery.date;
  const disabledDatesFromBilling = useDisabledDatesByBillingDays(selectedTime ?? nextDeliveryDate);

  const now = useMemo(() => new Date(), []);
  const tomorrow = useMemo(() => {
    const date = new Date(now);
    return new Date(date.setDate(date.getDate() + 1));
  }, []);
  const disabledDate = useMemo(() => resetTimeOnDate(tomorrow), []);

  const onSetNextDeliveryDate = useCallback(
    async (date: Date) => {
      setIsUpdatingNextDeliveryDate(true);
      if (disabledDate && +date >= +disabledDate) {
        setShowDatePicker(false);
        setTimeout(async () => {
          closeModal();
          await dispatch(
            updateNextShipmentDate(
              delivery.purchaseState.id,
              adjustToStoreBillingTime(date, organization!.billingTime, organization!.billingTimezone)
            )
          );

          toast(localize(`${localizeShorthand}.delayDateView.dateChangedToast`));
        }, 0);
      }
      setIsUpdatingNextDeliveryDate(false);
    },
    [delivery.purchaseState.id, dispatch, disabledDate, organization]
  );
  const selectDate = useCallback((date: Date, index: number) => {
    // If selecting the last button, which should always be the date picker, expand date picker
    if (index === delayButtons.length - 1) {
      setShowDatePicker(!showDatePicker);
    }
    if (active === index) {
      setActive(-1);
      setSelectedTime(null);
    } else if (active === -1) {
      setActive(index);
      setSelectedTime(date);
    }
  }, []);

  const convertDate = useCallback(
    (i: number) => {
      setShowDatePicker(false);
      let date = new Date(nextDeliveryDate);
      date.setDate(nextDeliveryDate.getDate() + (i + 1) * 7);
      date = updateDateRecur(disabledDatesFromBilling, date);

      selectDate(date, i);
    },
    [nextDeliveryDate]
  );
  const delayButtons = [
    { label: "delayOneBtn" },
    { label: "delayTwoBtn" },
    { label: "delayThreeBtn" },
    { label: "customDateBtn" },
  ];

  const isValidDate =
    selectedTime &&
    selectedTime?.toLocaleDateString() !== now.toLocaleDateString() &&
    selectedTime > now &&
    selectedTime.toLocaleDateString() !== nextDeliveryDate.toLocaleDateString();

  const saveUserPauseData = useCallback(
    async (userPauseReason: string, id?: string) => {
      const purchaseState = delivery.purchaseState;
      const userPause: IUserPause = {
        reasonText: userPauseReason || "",
        stId: purchaseState.id,
        pauseReasonId: id || "",
        actionType: PauseActionType.CHANGE_DATE,
      };

      await saveUserPauseReasonAPI(userPause)
        .then(res => {
          if (res.type === "error") {
            captureException("Error saving user's pause reason", res.message);
          }
        })
        .catch(error => {
          captureException("Error saving user's pause reason", error);
        });
    },
    [userPauseReasonText, selectedPauseReason]
  );

  if (isUpdatingNextDeliveryDate) {
    return (
      <Stack justifyContent="center" alignContent="center">
        <DotSpinner />
      </Stack>
    );
  }

  return (
    <Box>
      {phase === "reason-picker" && (
        <Stack spacing="15px" justifyContent="center" alignItems="center" sx={{ marginTop: "5px" }}>
          <CenterText title={localize(`${localizeShorthand}.delayDateView.reasonTitle`)}></CenterText>
          <PauseReasonPicker
            selectedPauseReason={selectedPauseReason}
            setSelectedPauseReason={setSelectedPauseReason}
            userPauseReasonText={userPauseReasonText}
            setUserPauseReasonText={setUserPauseReasonText}
            placeholderText={localize(`${localizeShorthand}.delayDateView.textAreaPlaceholder`)}
          />
          <ConfirmCancelButtons
            onCancel={setIconButtonInactive}
            onConfirm={() => setPhase("delay-picker")}
            confirmTextOverride="general.nextBtn"
          />
        </Stack>
      )}
      {phase === "delay-picker" && (
        <React.Fragment>
          <Stack spacing="15px" direction="column" justifyContent="center">
            <React.Fragment>
              {delayButtons.map((ele, i) => (
                <AquariusButton
                  key={i}
                  onClick={() =>
                    i === delayButtons.length - 1 ? selectDate(now, delayButtons.length - 1) : convertDate(i)
                  }
                  selected={active === i}
                >
                  {localize(`${localizeShorthand}.delayDateView.${ele.label}`)}
                </AquariusButton>
              ))}
            </React.Fragment>

            <Box ref={dateBoxRef} sx={{ overflow: "hidden", marginTop: "0px !important" }}>
              {!!showDatePicker && (
                <Slide in={showDatePicker} direction="down" timeout={500} container={dateBoxRef.current}>
                  <div>
                    <AquariusDatePicker
                      initialMonth={nextDeliveryDate}
                      disabledDaysBefore={disabledDate}
                      selectedTime={selectedTime}
                      setSelectedTime={setSelectedTime}
                      selectDate={selectDate}
                      delayButtons={delayButtons}
                      currentDeliveryDate={nextDeliveryDate}
                    />
                  </div>
                </Slide>
              )}
            </Box>
          </Stack>
          <Stack justifyContent="center" textAlign="center" sx={{ marginTop: "15px" }}>
            {!!isValidDate && (
              <Typography variant="caption1" sx={{ opacity: "70%", margin: "0 auto" }}>
                {localize(
                  `${localizeShorthand}.delayDateView.nextOrderDisplay`,
                  selectedTime?.toLocaleDateString() === tomorrow.toLocaleDateString()
                    ? {
                        date: typedLocalize("dates.tomorrow"),
                      }
                    : {
                        date: typedLocalize("utils.dates.toViewDateShort", {
                          date: selectedTime,
                        }),
                      }
                )}
              </Typography>
            )}
            <ConfirmCancelButtons
              onCancel={setIconButtonInactive}
              onConfirm={async () => {
                await onSetNextDeliveryDate(selectedTime!);
                if (cancellationReason && saveUserCancellationData) {
                  saveUserCancellationData();
                }
                await saveUserPauseData(userPauseReasonText || "", selectedPauseReason?.id);
              }}
              disabled={!isValidDate}
            />
          </Stack>
        </React.Fragment>
      )}
    </Box>
  );
};
